/**
 * The typed API client.
 *
 * Three decisions shape it, and they match what `apps/api` actually does:
 *
 * 1. **The refresh token is never touched by JavaScript.** It lives in an
 *    httpOnly cookie scoped to `/api/auth`, so every request goes out with
 *    `credentials: 'include'` and the browser decides when to attach it. There
 *    is no token in `localStorage` — an XSS bug must not become a permanent
 *    account takeover.
 * 2. **The access token lives in memory only.** It is short-lived (15 minutes
 *    by default) and dies with the tab; a reload re-establishes the session
 *    from the cookie via `POST /auth/refresh`.
 * 3. **Refresh is single-flight.** The API *rotates* refresh tokens and treats
 *    a replayed one as theft — it revokes the whole family. Two concurrent
 *    refreshes would therefore log the user out, so every caller joins one
 *    in-flight promise. This is also what makes React's StrictMode double-mount
 *    harmless in development.
 *
 * Every response is parsed with the zod schema the endpoint promises, so a
 * contract drift shows up here rather than as `undefined` in a component.
 */
import { loginResponseSchema, type LoginResponse } from '@wend/shared';
import type { z } from 'zod';

import { API_BASE_URL } from '../config';
import { ApiError } from './errors';

/* -------------------------------------------------------------------------- */
/* Session memory                                                              */
/* -------------------------------------------------------------------------- */

let accessToken: string | null = null;

export type SessionListener = (session: LoginResponse | null) => void;

const listeners = new Set<SessionListener>();

/**
 * Subscribes to session changes the client makes on its own — a background
 * refresh succeeding, or a refresh failing and dropping the session. The auth
 * provider uses it so component state never disagrees with the token in memory.
 */
export function onSessionChange(listener: SessionListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function publish(session: LoginResponse | null): void {
  accessToken = session === null ? null : session.accessToken;
  for (const listener of listeners) {
    listener(session);
  }
}

/** Adopt a session the caller established (login, or post-set-password login). */
export function adoptSession(session: LoginResponse): void {
  publish(session);
}

/** Forget the session locally. Does not call the API. */
export function clearSession(): void {
  publish(null);
}

/* -------------------------------------------------------------------------- */
/* Requests                                                                    */
/* -------------------------------------------------------------------------- */

export interface RequestOptions<TSchema extends z.ZodTypeAny> {
  path: string;
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /**
   * JSON body. Serialised as-is; validate it with a contract before calling.
   *
   * A `FormData` is sent verbatim instead, with no `Content-Type` header — the
   * browser has to write that itself so the multipart boundary matches the body.
   * That is how the dataset upload posts a real file.
   */
  body?: unknown;
  /** Appended as a query string. `undefined` values are dropped. */
  query?: Record<string, string | undefined>;
  /** The contract the response must satisfy. */
  schema: TSchema;
  /**
   * Send the access token, and on a 401 refresh once and retry. Defaults to
   * true; the endpoints that *establish* a session pass false.
   */
  auth?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: Record<string, string | undefined>): string {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (query !== undefined) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

async function send(
  options: Pick<RequestOptions<z.ZodTypeAny>, 'path' | 'method' | 'body' | 'query' | 'signal'>,
  bearer: string | null,
): Promise<Response> {
  const multipart = options.body instanceof FormData;

  const headers: Record<string, string> = { Accept: 'application/json' };
  // Setting Content-Type by hand on a multipart body omits the boundary and the
  // server then parses nothing, so the browser is left to write that one.
  if (options.body !== undefined && !multipart) headers['Content-Type'] = 'application/json';
  if (bearer !== null) headers.Authorization = `Bearer ${bearer}`;

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    // Without this the httpOnly refresh cookie is never sent, and refresh and
    // logout silently do nothing.
    credentials: 'include',
  };
  if (options.body !== undefined) {
    // A FormData can be handed to `fetch` more than once, so the 401 → refresh →
    // retry path re-sends the same upload rather than losing the file.
    init.body = multipart ? (options.body as FormData) : JSON.stringify(options.body);
  }
  if (options.signal !== undefined) init.signal = options.signal;

  try {
    return await fetch(buildUrl(options.path, options.query), init);
  } catch (cause) {
    // An aborted request is the caller's own doing, not a network failure.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw ApiError.network(cause);
  }
}

async function decode<TSchema extends z.ZodTypeAny>(
  response: Response,
  schema: TSchema,
): Promise<z.output<TSchema>> {
  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  let payload: unknown;
  try {
    payload = response.status === 204 ? undefined : await response.json();
  } catch (cause) {
    throw ApiError.malformedResponse(cause);
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw ApiError.malformedResponse(parsed.error);
  }
  return parsed.data;
}

/**
 * Performs a request and validates the response against its contract.
 *
 * On a 401 from an authenticated route it refreshes once and retries — the
 * access token expiring mid-session is routine, not an error the UI should see.
 * If the refresh also fails the session is dropped and the 401 propagates, and
 * the route guard sends the user to `/login`.
 */
export async function apiRequest<TSchema extends z.ZodTypeAny>(
  options: RequestOptions<TSchema>,
): Promise<z.output<TSchema>> {
  const authenticated = options.auth ?? true;

  const response = await send(options, authenticated ? accessToken : null);

  if (response.status !== 401 || !authenticated) {
    return decode(response, options.schema);
  }

  const refreshed = await refreshSession();
  if (refreshed === null) {
    throw await ApiError.fromResponse(response);
  }

  const retried = await send(options, refreshed.accessToken);
  return decode(retried, options.schema);
}

/**
 * Fetches raw bytes from an authenticated route — the inline images the template
 * editor shows, and from slice 13 the report workbooks a finished run produced.
 *
 * The image case is what it was written for and is described below; the report
 * case is the same argument with a different ending. A report's bytes are behind
 * the same tenant guard, and the storage port deliberately hands out no URL for
 * a stored object, so there is no link to give and an `<a href>` would save a
 * 401 envelope under an `.xlsx` name. What differs is who owns the object URL
 * afterwards: an `<img>` keeps it for as long as it is on screen, while a
 * download is finished with it immediately — `saveBlobAs` in `lib/download.ts`
 * is where that half lives.
 *
 * It exists because those bytes are behind the tenant guard. An
 * `<img src="/api/templates/…">` in the page carries no `Authorization` header,
 * so it gets a 401 and renders as a broken image; the bytes have to be fetched
 * with the access token and handed to the `<img>` as an object URL. **The caller
 * owns that URL and must revoke it** — an unrevoked one keeps the blob alive for
 * the life of the document.
 *
 * `apiRequest` cannot do this job: it parses JSON against a contract, and an
 * image has no contract to parse. What the two share is the part that matters —
 * the same in-memory bearer, the same single-flight refresh, and the same
 * refresh-once-and-retry on a 401.
 */
export async function apiBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const options = { path, method: 'GET' as const, ...(signal === undefined ? {} : { signal }) };

  let response = await send(options, accessToken);

  if (response.status === 401) {
    const refreshed = await refreshSession();
    if (refreshed === null) throw await ApiError.fromResponse(response);
    response = await send(options, refreshed.accessToken);
  }

  if (!response.ok) throw await ApiError.fromResponse(response);

  try {
    return await response.blob();
  } catch (cause) {
    throw ApiError.malformedResponse(cause);
  }
}

/* -------------------------------------------------------------------------- */
/* Refresh                                                                     */
/* -------------------------------------------------------------------------- */

let inFlightRefresh: Promise<LoginResponse | null> | null = null;

/**
 * Exchanges the httpOnly refresh cookie for a new access token.
 *
 * Returns `null` — rather than throwing — when there is simply no valid session
 * to resume: an anonymous first visit is the expected case on app start, not a
 * failure. A malformed response or an unreachable API still throws.
 */
export function refreshSession(): Promise<LoginResponse | null> {
  if (inFlightRefresh !== null) return inFlightRefresh;

  const attempt = (async (): Promise<LoginResponse | null> => {
    const response = await send({ path: '/auth/refresh', method: 'POST' }, null);

    if (response.status === 401 || response.status === 403) {
      publish(null);
      return null;
    }

    const session = await decode(response, loginResponseSchema);
    publish(session);
    return session;
  })();

  inFlightRefresh = attempt.finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}
